import type { CSSObject } from "type/tss";
import { Theme } from "@mui/material/styles";

/**
 * Shared styles for the refreshed settings pages.
 *
 * Every refreshed page had been redefining pageSubtitle / sectionHeading / labelTitle in its own
 * style.ts, which is why they had drifted apart. Spread these into a page's styles instead:
 *
 *   export const styles = (theme: Theme) => ({ ...settingsPageStyles(theme), myOwnThing: {...} })
 */
export const settingsPageStyles = (theme: Theme) => {
	return ({
		pageSubtitle: {
			color: theme.palette.custom.textSecondary,
			marginBottom: 24,
		},
		sectionHeading: {
			fontWeight: "bold",
			fontSize: 18,
			color: theme.palette.custom.textPrimary,
			marginBottom: 4,
		},
		sectionDescription: {
			fontSize: 16,
			color: theme.palette.custom.textPrimary,
			marginBottom: 16,
		},
		// A SettingsSection title sits 24px above whatever follows it, description or content
		// (MAX-4337, MAX-4355).
		sectionTitle: {
			marginBottom: 24,
		},
		labelTitle: {
			fontWeight: "bold",
			fontSize: 16,
			color: theme.palette.custom.textPrimary,
		},
		labelDescription: {
			fontSize: 16,
			color: theme.palette.custom.textPrimary,
			marginTop: 4,
		},
		// The designs put form fields in a fixed-width column inside the full-width card rather
		// than stretching them across it.
		fieldColumn: {
			maxWidth: 576,
			width: "100%",
		},
		// Fields revealed by a checkbox sit indented under it.
		disclosedFields: {
			marginLeft: 40,
		},
		readOnlyLabel: {
			fontSize: 14,
			color: theme.palette.custom.textSecondary,
			marginBottom: 2,
		},
		readOnlyValue: {
			fontSize: 16,
			color: theme.palette.custom.textPrimary,
		},
	} satisfies Record<string, CSSObject>);
};

// The Figma "Section" frames space title, description and content 24px apart. Opt-in through
// SettingsSection's `refreshed` (MAX-4419), and kept out of settingsPageStyles, which pages also
// spread into headings outside a section card.
export const settingsSectionStyles = (theme: Theme) => {
	return ({
		...settingsPageStyles(theme),
		refreshedGap: {
			marginBottom: theme.spacing(3),
		},
	} satisfies Record<string, CSSObject>);
};
